import * as React from "react";
import { useNavigate } from "react-router";
import { Loader } from "@/components/ui/loader";
import * as analytics from "@/services/analytics";

import {
  useDeletePromptLibraryItemsMutation,
  useGetPromptLibraryItemsQuery,
  useTogglePromptLibraryItemFavoriteMutation,
} from "../promptLibrary.api";
import { PromptLibraryItem, PromptLibraryTab } from "@/modules/promptLibrary/promptLibrary.types";
import { Button } from "@/components/ui/button";
import { ModifyNewPromptLibraryItemDialog } from "@/modules/promptLibrary/components/modifyNewPromptLibraryItemDialog";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { ApplyPromptLibraryItemDialog } from "@/modules/promptLibrary/components/applyPromptLibraryItemDialog";
import { usePromptLibraryContext } from "@/modules/promptLibrary/usePromptLibraryContext";
import { useGetDirectoryUsersQuery } from "@/api/users-admin";
import { ConfirmDeletePromptLibraryDialog } from "@/modules/promptLibrary/components/confirmDeletePromptLibraryItemDialog";
import { createGetItemsPayload } from "@/modules/promptLibrary/promptLibrary.utils";
import { PromptLibraryTableItem } from "@/modules/promptLibrary/components/PromptLibraryTableItem";

interface Props {
  activeTab: PromptLibraryTab;
  searchPhrase: string;
}

export const PromptLibraryTable: React.FC<Props> = ({ activeTab, searchPhrase }) => {
  const { id: userId } = useAuthenticatedUser();

  const getItemsPayload = createGetItemsPayload(searchPhrase, activeTab);

  const { data, isLoading } = useGetPromptLibraryItemsQuery(getItemsPayload);
  const [deletePromptLibraryItems] = useDeletePromptLibraryItemsMutation();
  const [togglePromptLibraryItemFavoriteMutation] = useTogglePromptLibraryItemFavoriteMutation();
  const { setPromptToApply } = usePromptLibraryContext();
  const navigate = useNavigate();
  const userIds = React.useMemo(() => {
    if (!data) {
      return [];
    }

    return data.map(item => item.user_id);
  }, [data]);
  const userQuery = useGetDirectoryUsersQuery(userIds);

  const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
  const [editingPromptItem, setEditingPromptItem] = React.useState<PromptLibraryItem | null>(null);
  const [promptItemToDelete, setPromptItemToDelete] = React.useState<PromptLibraryItem | null>(null);
  const [isApplyModalOpen, setIsApplyModalOpen] = React.useState(false);

  const handleFavoriteClick = async ({ id, user_id }: PromptLibraryItem) => {
    if (user_id !== userId) {
      return;
    }
    await togglePromptLibraryItemFavoriteMutation(id);
  };

  const handleEditClick = async (item: PromptLibraryItem) => {
    if (item.user_id !== userId) {
      return;
    }

    setEditingPromptItem(item);
  };

  const handleApplyClick = (item: PromptLibraryItem) => {
    setPromptToApply({
      source: item.source,
      prompt: item.prompt,
    });

    if (item.source === "qa") {
      setIsApplyModalOpen(true);
    } else {
      navigate("/open-chat");
    }
  };

  const handleTrashClick = async (item: PromptLibraryItem) => {
    if (item.user_id !== userId) {
      return;
    }
    setPromptItemToDelete(item);
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader className="h-8 w-8" />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between">
        <h1 className="mb-5 text-2xl font-semibold">Prompt Library</h1>
        <Button
          onClick={() => {
            analytics.addPromptToLibrary();
            setIsAddModalOpen(true);
          }}
        >
          Add new prompt
        </Button>
      </div>

      <div className="flex flex-col gap-4">
        {data!.map(item => {
          const user = userQuery.data?.find(({ id }) => id === item.user_id);
          return (
            <PromptLibraryTableItem
              key={item.id}
              item={item}
              user={user!}
              onEdit={handleEditClick}
              onDelete={handleTrashClick}
              onFavorite={handleFavoriteClick}
              onSendToChat={handleApplyClick}
            ></PromptLibraryTableItem>
          );
        })}
      </div>

      {isAddModalOpen && (
        <ModifyNewPromptLibraryItemDialog
          isOpen={isAddModalOpen}
          setIsOpen={value => setIsAddModalOpen(value)}
          mode="add"
        />
      )}
      {editingPromptItem && (
        <ModifyNewPromptLibraryItemDialog
          isOpen={!!editingPromptItem}
          setIsOpen={() => setEditingPromptItem(null)}
          item={editingPromptItem}
          mode="edit"
        />
      )}
      {isApplyModalOpen && (
        <ApplyPromptLibraryItemDialog
          isOpen={isApplyModalOpen}
          setIsOpen={value => {
            setPromptToApply(null);
            setIsApplyModalOpen(value);
          }}
        />
      )}
      {promptItemToDelete && (
        <ConfirmDeletePromptLibraryDialog
          isOpen={!!promptItemToDelete}
          setIsOpen={() => setPromptItemToDelete(null)}
          name={promptItemToDelete.name}
          confirmCb={() => {
            deletePromptLibraryItems(promptItemToDelete.id);
            setPromptItemToDelete(null);
          }}
        ></ConfirmDeletePromptLibraryDialog>
      )}
    </>
  );
};
